import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BsFilePdf, BsCloudArrowUp, BsShield, BsCheck, BsDropbox, BsDownload, BsArrowRepeat } from 'react-icons/bs';
import { FiUploadCloud } from 'react-icons/fi';
import SEO from '../components/SEO';
import { compressPdfSchemas } from '../schemas';
import API_BASE_URL from '../config';
import './CompressPDF.css';

const CompressPDF = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [quality, setQuality] = useState('medium');
  const [isCompressing, setIsCompressing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      setError(null);
      setResult(null);
    } else {
      setError('Please upload a valid PDF file');
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setError(null);
      setResult(null);
    } else {
      setError('Please upload a valid PDF file');
    }
  };

  const handleCompress = async () => {
    if (!file) return;

    setIsCompressing(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('pdf', file);
    formData.append('quality', quality);

    try {
      const startTime = Date.now();

      const response = await fetch(`${API_BASE_URL}/api/compress-pdf`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Compression failed');
      }

      const data = await response.json();

      // Ensure minimum 3 seconds for animation
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(3000 - elapsedTime, 0);
      await new Promise(resolve => setTimeout(resolve, remainingTime));

      setResult(data);
    } catch (err) {
      console.error('Compression error:', err);
      setError(err.message || 'Failed to compress PDF. Please try again.');
    } finally {
      setIsCompressing(false);
    }
  };

  const handleDownload = () => {
    if (!result) return;

    // Convert base64 to blob
    const byteCharacters = atob(result.compressedPdf);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `compressed_${result.filename}`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setError(null);
    setQuality('medium');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const securityFeatures = [
    { icon: <BsShield />, text: 'Secure SSL Connection' },
    { icon: <BsCheck />, text: 'Max file size: 100 MB' },
    { icon: <BsCheck />, text: 'Files deleted after 2h' }
  ];

  return (
    <div className="compress-pdf-page">
      <SEO 
        title="Compress PDF Online – Reduce PDF File Size Free"
        description="Shrink large PDF files online without losing quality. Fast, secure PDF compressor — reduce file size for email or upload in seconds."
        keywords="compress pdf, reduce pdf size, pdf compressor online"
        path="/compress-pdf"
        schema={compressPdfSchemas}
      />
      <section className="tool-hero">
        <div className="tool-hero-container">
          <motion.div 
            className="tool-icon-large"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <BsFilePdf />
          </motion.div>

          <motion.h1
            className="compress-tool-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Compress PDF
          </motion.h1>
        </div>
      </section>

      <section className="upload-section">
        <div className="upload-container">
          {!file && !result ? (
            <>
              <motion.div
                className={`upload-dropzone ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                onClick={() => fileInputRef.current?.click()}
              >
                <motion.div 
                  className="upload-icon"
                  animate={{ 
                    y: [0, -10, 0],
                  }}
                  transition={{ 
                    duration: 2, 
                    repeat: Infinity, 
                    ease: 'easeInOut' 
                  }}
                >
                  <FiUploadCloud />
                </motion.div>

                <h2 className="upload-title">Select PDF File</h2>
                <p className="upload-description">
                  Drag and drop your PDF file here, or click<br />
                  to browse from your device
                </p>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />

                <div className="upload-buttons">
                  <motion.button 
                    className="btn-primary-upload"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                  >
                    Choose File
                  </motion.button>
                </div>
              </motion.div>

              {error && (
                <motion.div
                  className="error-message"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {error}
                </motion.div>
              )}
            </>
          ) : result ? (
            <motion.div
              className="result-container"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="result-card">
                <div className="result-icon success">
                  <BsCheck />
                </div>
                
                <h2 className="result-title">Compression Successful!</h2>
                
                <div className="result-stats">
                  <div className="stat-item">
                    <span className="stat-label">Original Size</span>
                    <span className="stat-value">{formatFileSize(result.originalSize)}</span>
                  </div>
                  
                  <div className="stat-arrow">→</div>
                  
                  <div className="stat-item">
                    <span className="stat-label">Compressed Size</span>
                    <span className="stat-value success">{formatFileSize(result.compressedSize)}</span>
                  </div>
                </div>

                <div className="compression-ratio">
                  <span className="ratio-label">Compression Ratio:</span>
                  <span className="ratio-value">{result.compressionRatio}%</span>
                </div>

                <div className="result-actions">
                  <motion.button
                    className="btn-download"
                    onClick={handleDownload}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <BsDownload /> Download Compressed PDF
                  </motion.button>
                  
                  <motion.button
                    className="btn-reset"
                    onClick={handleReset}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <BsArrowRepeat /> Compress Another
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              className="compression-panel"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="file-preview">
                <BsFilePdf className="file-preview-icon" />
                <div className="file-info">
                  <h3>{file.name}</h3>
                  <p>{formatFileSize(file.size)}</p>
                </div>
              </div>

              <div className="quality-selector">
                <h3>Select Compression Quality</h3>
                <div className="quality-options">
                  <motion.button
                    className={`quality-btn ${quality === 'low' ? 'active' : ''}`}
                    onClick={() => setQuality('low')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="quality-name">Low Quality</span>
                    <span className="quality-desc">72 DPI - Smallest size</span>
                  </motion.button>

                  <motion.button
                    className={`quality-btn ${quality === 'medium' ? 'active' : ''}`}
                    onClick={() => setQuality('medium')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="quality-name">Medium Quality</span>
                    <span className="quality-desc">150 DPI - Balanced</span>
                  </motion.button>

                  <motion.button
                    className={`quality-btn ${quality === 'high' ? 'active' : ''}`}
                    onClick={() => setQuality('high')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="quality-name">High Quality</span>
                    <span className="quality-desc">300 DPI - Best quality</span>
                  </motion.button>
                </div>
              </div>

              {error && (
                <motion.div
                  className="error-message"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {error}
                </motion.div>
              )}

              <div className="compression-actions">
                <motion.button
                  className="btn-compress"
                  onClick={handleCompress}
                  disabled={isCompressing}
                  whileHover={{ scale: isCompressing ? 1 : 1.05 }}
                  whileTap={{ scale: isCompressing ? 1 : 0.95 }}
                  animate={isCompressing ? {
                    boxShadow: [
                      '0 4px 20px var(--primary-shadow)',
                      '0 6px 30px var(--primary-shadow)',
                      '0 4px 20px var(--primary-shadow)'
                    ]
                  } : {}}
                  transition={{ duration: 1, repeat: isCompressing ? Infinity : 0 }}
                >
                  {isCompressing ? (
                    <>
                      Compressing<span className="dots">...</span>
                    </>
                  ) : (
                    'Compress PDF'
                  )}
                </motion.button>

                <motion.button
                  className="btn-cancel"
                  onClick={handleReset}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Cancel
                </motion.button>
              </div>
            </motion.div>
          )}

          <motion.div
            className="security-badges"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {securityFeatures.map((feature, index) => (
              <motion.div 
                key={index}
                className="security-badge"
                whileHover={{ scale: 1.05 }}
              >
                <span className="security-icon">{feature.icon}</span>
                <span className="security-text">{feature.text}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CompressPDF;
